import { Money } from "@hubrise/react-components"
import _ from "lodash"

import { clearAllPrices, bulkUpdatePrices } from "shared/hubrise/editor/utils"
import { CategoryId, OptionListId, ProductId, SkuId } from "shared/hubrise/ids"
import { IProduct, ISku } from "shared/hubrise/models"
import * as Product from "shared/hubrise/utils/Product"
import * as Sku from "shared/hubrise/utils/Sku"
import { findWithProductById } from "shared/hubrise/utils/Sku"
import { GenerateRefsOptions } from "shared/hubrise/utils/ref"

import type { Change, OperationContext } from "./types"

export type SkuOperations = {
  add(product: IProduct, sku: ISku): void
  delete(productId: ProductId, skuId: SkuId): void
  detachOptionList(productId: ProductId, skuId: SkuId, optionListId: OptionListId): void
  reorderOptionLists(productId: ProductId, skuId: SkuId, optionListIds: Array<OptionListId>): void
  update<T extends keyof ISku>(
    productId: ProductId,
    skuId: SkuId,
    fieldName: T,
    value: ISku[T],
    options?: { collapse?: boolean },
  ): void
  removeAllRefs(): void
  generateRefs(options: GenerateRefsOptions): void
  enable(productId: ProductId, skuId: SkuId, enabled: boolean, variantRef?: string): void
  enableAll(enabled: boolean, variantRef?: string): void
  updateVariantPrice(productId: ProductId, skuId: SkuId, variantRef: string, price: Money | null): void
  bulkUpdatePrices(
    sourceVariantRef: string | null,
    targetVariantRef: string | null,
    skipAlreadySetPrices: boolean,
    categoryIds: Array<CategoryId> | null,
    calculateTargetPrice: (sourcePrice: Money) => Money,
  ): void
  clearAllPrices(variantRef: string): void
}

export default ({ catalog, pushState }: OperationContext): SkuOperations => ({
  add(product, sku) {
    const newCatalog = Product.update(catalog, product.id, "skus", [...product.skus, sku])
    pushState(newCatalog, { model: "sku", operation: "add" })
  },

  delete(productId, skuId) {
    const newCatalog = Sku.doDelete(catalog, productId, skuId)
    pushState(newCatalog, { model: "sku", operation: "delete", id: skuId })
  },

  detachOptionList(productId, skuId, optionListId) {
    const newCatalog = Sku.detachOptionList(catalog, productId, skuId, optionListId)
    pushState(newCatalog, { model: "sku", operation: "detachOptionList", id: skuId, optionListId: optionListId })
  },

  reorderOptionLists(productId, skuId, optionListIds) {
    const newCatalog = Sku.update(catalog, productId, skuId, "optionListIds", optionListIds)
    const change: Change = {
      model: "sku",
      id: skuId,
      operation: "reorderOptionLists",
    }
    pushState(newCatalog, change, (previousChange) => _.isEqual(previousChange, change))
  },

  update(productId, skuId, fieldName, value, { collapse = true } = {}) {
    const newCatalog = Sku.update(catalog, productId, skuId, fieldName, value)
    const change: Change = {
      model: "sku",
      id: skuId,
      operation: "update",
      fieldName,
    }
    pushState(newCatalog, change, (previousChange) => collapse && _.isEqual(previousChange, change))
  },

  removeAllRefs() {
    const newCatalog = Sku.removeAllRefs(catalog)
    pushState(newCatalog, { model: "sku", operation: "removeAllRefs" })
  },

  generateRefs(options) {
    const newCatalog = Sku.generateRefs(catalog, options)
    pushState(newCatalog, { model: "sku", operation: "generateRefs" })
  },

  enable(productId, skuId, enabled, variantRef) {
    const newCatalog = Sku.enable(catalog, productId, skuId, enabled, variantRef)
    const change: Change = {
      model: "sku",
      id: skuId,
      operation: "enable",
      variantRef,
    }
    pushState(newCatalog, change, (previousChange) => _.isEqual(previousChange, change))
  },

  enableAll(enabled, variantRef) {
    const newCatalog = Sku.enableAll(catalog, enabled, variantRef)
    const change: Change = { model: "sku", operation: "enableAll", variantRef }
    pushState(newCatalog, change)
  },

  updateVariantPrice(productId, skuId, variantRef, price) {
    const newCatalog = Sku.setVariantPrice(catalog, productId, skuId, variantRef, price)
    const change: Change = {
      model: "sku",
      id: skuId,
      operation: "updateVariantPrice",
      variantRef,
    }
    pushState(newCatalog, change, (previousChange) => _.isEqual(previousChange, change))
  },

  bulkUpdatePrices(sourceVariantRef, targetVariantRef, skipAlreadySetPrices, categoryIds, calculateTargetPrice) {
    const newCatalog = bulkUpdatePrices<ISku>(
      "products",
      catalog,
      sourceVariantRef,
      targetVariantRef,
      skipAlreadySetPrices,
      calculateTargetPrice,
      categoryIds ? (sku) => categoryIds.includes(findWithProductById(catalog, sku.id).product.categoryId) : undefined,
    )
    const change: Change = { model: "sku", operation: "bulkUpdatePrices" }
    pushState(newCatalog, change)
  },

  clearAllPrices(variantRef: string) {
    const newCatalog = clearAllPrices("products", catalog, variantRef)
    const change: Change = { model: "sku", operation: "bulkUpdatePrices" }
    pushState(newCatalog, change)
  },
})
